/**
 * Pure business logic for Home Page
 * ตรรกะธุรกิจสำหรับหน้าหลัก (Pure functions เท่านั้น)
 */

import type { Project, StatusFilter } from './types/home.types'

/**
 * Get Thai greeting based on current hour
 * คำทักทายตามช่วงเวลา
 *
 * @returns Thai greeting string (สวัสดีตอนเช้า/บ่าย/เย็น)
 */
export function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'สวัสดีตอนเช้า'
  if (hour < 17) return 'สวัสดีตอนบ่าย'
  return 'สวัสดีตอนเย็น'
}

/**
 * Get greeting for a specific hour (for testing)
 *
 * @param hour - Hour of day (0-23)
 * @returns Thai greeting string
 */
export function getGreetingForHour(hour: number): string {
  if (hour < 0 || hour > 23) {
    throw new Error('Hour must be between 0 and 23')
  }
  if (hour < 12) return 'สวัสดีตอนเช้า'
  if (hour < 17) return 'สวัสดีตอนบ่าย'
  return 'สวัสดีตอนเย็น'
}

/**
 * Calculate project progress percentage
 * คำนวณเปอร์เซ็นต์ความคืบหน้า
 *
 * @param done - Number of completed tasks
 * @param total - Total number of tasks
 * @returns Progress percentage (0-100)
 */
export function calculateProgress(done: number, total: number): number {
  if (total === 0) return 0
  if (done < 0 || total < 0) return 0
  if (done > total) return 100
  return Math.round((done / total) * 100)
}

/**
 * Get progress bar color class based on completion percentage
 * สีของแถบความคืบหน้าตามเปอร์เซ็นต์
 *
 * @param progress - Completion percentage (0-100)
 * @returns Tailwind CSS color class
 */
export function getProgressColor(progress: number): string {
  if (progress >= 80) return 'bg-emerald-500'
  if (progress >= 40) return 'bg-amber-500'
  return 'bg-slate-400'
}

/**
 * Status configuration for badges
 * การกำหนดค่าสีสำหรับแต่ละสถานะ
 */
export const STATUS_CONFIG: Record<string, { label: string; bg: string; fg: string }> = {
  'in-progress': {
    label: 'กำลังดำเนินการ',
    bg: 'bg-amber-100',
    fg: 'text-amber-800',
  },
  completed: {
    label: 'เสร็จสิ้น',
    bg: 'bg-emerald-100',
    fg: 'text-emerald-800',
  },
  'not-started': {
    label: 'ยังไม่เริ่ม',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
  },
  reviewed: {
    label: 'ตรวจสอบแล้ว',
    bg: 'bg-emerald-100',
    fg: 'text-emerald-800',
  },
}

/**
 * Get status display config
 *
 * @param status - Project status
 * @returns Status configuration object
 */
export function getStatusConfig(status: string) {
  return (
    STATUS_CONFIG[status] ?? {
      label: status,
      bg: 'bg-gray-100',
      fg: 'text-gray-600',
    }
  )
}

/**
 * Map Thai status filter to English status value
 */
const STATUS_FILTER_MAP: Record<StatusFilter, string | null> = {
  ทั้งหมด: null,
  กำลังดำเนินการ: 'in-progress',
  เสร็จสิ้น: 'completed',
  ยังไม่เริ่ม: 'not-started',
}

/**
 * Filter projects by search query and status
 * กรองโครงการตามคำค้นหาและสถานะ
 *
 * @param projects - Array of projects
 * @param search - Search query string
 * @param statusFilter - Status filter option
 * @returns Filtered array of projects
 */
export function filterProjects(
  projects: Project[],
  search: string,
  statusFilter: StatusFilter
): Project[] {
  return projects.filter((project) => {
    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase()
      const matchesSearch =
        project.company.toLowerCase().includes(query) ||
        project.taxId.includes(query) ||
        project.year.includes(query)
      if (!matchesSearch) return false
    }

    // Status filter
    if (statusFilter === 'ทั้งหมด') return true
    const targetStatus = STATUS_FILTER_MAP[statusFilter as StatusFilter]
    return targetStatus === null || project.status === targetStatus
  })
}

/**
 * Paginate array of items
 * แบ่งหน้ารายการ
 *
 * @param items - Array to paginate
 * @param page - Current page number (1-based)
 * @param pageSize - Items per page
 * @returns Paginated slice of array
 */
export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  if (page < 1) return []
  if (pageSize < 1) return []
  const start = (page - 1) * pageSize
  return items.slice(start, start + pageSize)
}

/**
 * Calculate total pages
 * คำนวณจำนวนหน้าทั้งหมด
 *
 * @param totalItems - Total number of items
 * @param pageSize - Items per page
 * @returns Total number of pages
 */
export function calculateTotalPages(totalItems: number, pageSize: number): number {
  if (totalItems === 0) return 1
  if (pageSize < 1) return 1
  return Math.ceil(totalItems / pageSize)
}

/**
 * Get page numbers for pagination display
 * รายการหมายเลขหน้าสำหรับแสดง
 *
 * @param currentPage - Current active page
 * @param totalPages - Total number of pages
 * @param maxVisible - Maximum visible page numbers (default: 5)
 * @returns Array of page numbers
 */
export function getPageNumbers(currentPage: number, totalPages: number, maxVisible = 5): number[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const half = Math.floor(maxVisible / 2)
  let start = currentPage - half
  let end = currentPage + half

  if (start < 1) {
    start = 1
    end = maxVisible
  }

  if (end > totalPages) {
    end = totalPages
    start = totalPages - maxVisible + 1
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

/**
 * Sort ongoing projects by urgency
 * เรียงโครงการตามความเร่งด่วน
 *
 * @param projects - Array of ongoing projects
 * @returns Sorted array (urgent first, then by days left)
 */
export function sortByUrgency<T extends { urgent: boolean; daysLeft: number }>(projects: T[]): T[] {
  return [...projects].sort((a, b) => {
    if (a.urgent && !b.urgent) return -1
    if (!a.urgent && b.urgent) return 1
    return a.daysLeft - b.daysLeft
  })
}

/**
 * Extract first name from full name
 * ดึงชื่อแรกจากชื่อเต็ม
 *
 * @param fullName - Full name
 * @returns First name
 */
export function getFirstName(fullName: string): string {
  return fullName.split(' ')[0] ?? fullName
}
